import React, { useState, useEffect } from 'react';
import api from '../../services/apiService';
import { useNavigate } from 'react-router-dom';
import Header from '../../Header';
import './RegisterTeam.scss';

const RegisterTeam = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        teamName: '',
        type: 'squad',
        ageGroup: ''
    });
    const [adminData, setAdminData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        isPlayerManager: false
    });
    const [managerSearchMode, setManagerSearchMode] = useState('new'); // 'new' or 'search'
    const [managerSearchTerm, setManagerSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedManager, setSelectedManager] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [currentStage, setCurrentStage] = useState(1);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteCode, setInviteCode] = useState('');
    const [showAdminModal, setShowAdminModal] = useState(false);
    const [adminLoginDetails, setAdminLoginDetails] = useState(null);
    const [teamId, setTeamId] = useState(null);
    const totalStages = 2;

    // Effect to handle automatic player-manager setting for friendly teams
    useEffect(() => {
        if (formData.type === 'friendly') {
            setAdminData(prev => ({ ...prev, isPlayerManager: true }));
        }
    }, [formData.type]);





    const generateInviteCode = (teamId) => {
        let sum = 10;
        const code = [];

        // Generate the 4-digit code that sums to 10
        for (let i = 0; i < 3; i++) {
            const digit = Math.floor(Math.random() * (sum - (3 - i))) + 1;
            code.push(digit);
            sum -= digit;
        }
        code.push(sum);

        // Shuffle the code array
        for (let i = code.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [code[i], code[j]] = [code[j], code[i]];
        }

        // Format with team ID prefix
        return `${teamId}-${code.join('')}`;
    };

    const copyToClipboard = () => {
        const url = `${process.env.REACT_APP_PUBLIC_URL}/register?code=${inviteCode}`;
        navigator.clipboard.writeText(url).then(() => {
            alert('Invite link copied to clipboard!');
        }).catch(err => {
            console.error('Could not copy text: ', err);
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAdminChange = (e) => {
        const { name, value, type, checked } = e.target;
        setAdminData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };





    const searchManagers = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const response = await api.get(`/search_managers.php?term=${encodeURIComponent(searchTerm)}`);
            if (response.data.success) {
                setSearchResults(response.data.managers || []);
            } else {
                setSearchResults([]);
            }
        } catch (error) {
            console.error('Error searching managers:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleManagerSearch = (e) => {
        const term = e.target.value;
        setManagerSearchTerm(term);
        if (term.length >= 2) {
            searchManagers(term);
        } else {
            setSearchResults([]);
        }
    };

    const selectManager = (manager) => {
        setSelectedManager(manager);
        setAdminData({
            firstName: manager.first_name,
            lastName: manager.last_name,
            email: manager.email,
            phone: manager.phone ? `0${manager.phone}` : '',
            isPlayerManager: adminData.isPlayerManager
        });
        setManagerSearchTerm(`${manager.first_name} ${manager.last_name} (${manager.email})`);
        setSearchResults([]);
    };

    const clearManagerSelection = () => {
        setSelectedManager(null);
        setAdminData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            isPlayerManager: adminData.isPlayerManager
        });
        setManagerSearchTerm('');
        setSearchResults([]);
    };

    const handleTeamSubmit = (e) => {
        e.preventDefault();
        setCurrentStage(2);
    };

    const handlePrevStage = () => {
        setCurrentStage(currentStage - 1);
    };

    const handleNextStage = () => {
        setCurrentStage(currentStage + 1);
    };

    const handleAdminSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        // Validate that we have either a selected manager or filled form fields
        if (formData.type !== 'friendly' && managerSearchMode === 'search' && !selectedManager) {
            setError('Please select a manager from the search results');
            setIsSubmitting(false);
            return;
        }

        if ((formData.type !== 'friendly' && managerSearchMode === 'new') || formData.type === 'friendly') {
            if (!adminData.firstName || !adminData.lastName || !adminData.phone) {
                setError('Please fill in all required fields');
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const registrationData = {
                team: formData,
                admin: {
                    ...adminData,
                    isPlayerManager: formData.type === 'friendly' || adminData.isPlayerManager,
                    selectedManagerId: selectedManager ? selectedManager.manager_id : null
                }
            };

            const response = await api.post('/register_team.php', registrationData);
            let responseData = response.data;
            
            if (responseData && responseData.success) {
                setSuccess(true);
                setTeamId(responseData.team_id);
                
                if (registrationData.admin.isPlayerManager) {
                    // Use registration code directly from the response
                    if (responseData.registration_details && responseData.registration_details.registration_code) {
                        const code = responseData.registration_details.registration_code;
                        setInviteCode(code);
                        // Also capture login details for player-managers
                        setAdminLoginDetails({
                            phone: responseData.login_details?.phone || adminData.phone,
                            password: responseData.login_details?.password || 'Contact support for password'
                        });
                        setShowInviteModal(true);
                    } else {
                        // Fallback to generating a code if not provided
                        const code = generateInviteCode(responseData.team_id);
                        setInviteCode(code);
                        setShowInviteModal(true);
                    }
                } else {
                    // Regular admin - show login details modal
                    setAdminLoginDetails({
                        phone: responseData.login_details?.phone || adminData.phone,
                        password: responseData.login_details?.password || 'Contact support for password'
                    });
                    setShowAdminModal(true);
                }
            } else {
                // If we don't have success property or it's false
                const errorMsg = (responseData && responseData.message) ? 
                    responseData.message : 'Failed to complete registration';
                setError(errorMsg);
            }
        } catch (err) {
            if (err.response) {
                setError(err.response.data.message || 'Server error occurred');
            } else if (err.request) {
                setError('No response from server. Please check your connection.');
            } else {
                setError('Failed to send request. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };





    const renderStageIndicator = () => (
        <>
            <button className="back-button" onClick={() => window.history.back()}>← Back</button>
            <h1>Register New Team</h1>
            <div className="stage-indicator">
                {[...Array(totalStages)].map((_, index) => (
                    <div 
                        key={index} 
                        className={`stage ${currentStage === index + 1 ? 'active' : ''} ${currentStage > index + 1 ? 'completed' : ''}`}
                    >
                        {index + 1}
                    </div>
                ))}
            </div>
        </>
    );

    const renderTeamForm = () => (
        <form onSubmit={handleTeamSubmit} className="form-group">
            <h2>Team Details</h2>
            <div className="form-group">
                <label htmlFor="teamName">Team Name *</label>
                <input
                    type="text"
                    id="teamName"
                    name="teamName"
                    value={formData.teamName}
                    onChange={handleChange}
                    required
                    placeholder="Enter team name"
                    className="input-field"
                />
            </div>

            <div className="form-group">
                <label htmlFor="type">Team Type *</label>
                <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="input-field"
                >
                    <option value="academy">Academy</option>
                    <option value="squad">Squad (11v11)</option>
                    <option value="friendly">Friendly (5v5/6v6)</option>
                </select>
            </div>

            {formData.type === 'academy' && (
                <div className="form-group">
                    <label htmlFor="ageGroup">Age Group *</label>
                    <select
                        id="ageGroup"
                        name="ageGroup"
                        value={formData.ageGroup}
                        onChange={handleChange}
                        required
                        className="input-field"
                    >
                        <option value="">Select age group</option>
                        <option value="U7s">U7s</option>
                        <option value="U8s">U8s</option>
                        <option value="U9s">U9s</option>
                        <option value="U10s">U10s</option>
                        <option value="U11s">U11s</option>
                        <option value="U12s">U12s</option>
                        <option value="U13s">U13s</option>
                        <option value="U14s">U14s</option>
                        <option value="U15s">U15s</option>
                        <option value="U16s">U16s</option>
                        <option value="U17s">U17s</option>
                        <option value="U18s">U18s</option>
                        <option value="U21s">U21s</option>
                        <option value="U23s">U23s</option>
                    </select>
                </div>
            )}

            <div className="form-navigation">
                <button 
                    type="submit" 
                    className="next-stage-button"
                >
                    Next
                </button>
            </div>
        </form>
    );

    const renderAdminForm = () => (
        <form onSubmit={handleAdminSubmit} className="form-group">
            <h2>Your Details</h2>
            
            {formData.type !== 'friendly' && (
                <div className="form-group">
                    <label>Manager Selection</label>
                    <div className="manager-selection-tabs">
                        <button
                            type="button"
                            className={`tab-button ${managerSearchMode === 'new' ? 'active' : ''}`}
                            onClick={() => setManagerSearchMode('new')}
                        >
                            Add New Manager
                        </button>
                        <button
                            type="button"
                            className={`tab-button ${managerSearchMode === 'search' ? 'active' : ''}`}
                            onClick={() => setManagerSearchMode('search')}
                        >
                            Search Existing Manager
                        </button>
                    </div>
                </div>
            )}

            {formData.type !== 'friendly' && managerSearchMode === 'search' && (
                <div className="form-group">
                    <label htmlFor="managerSearch">Search Managers</label>
                    <div className="search-container">
                        <input
                            type="text"
                            id="managerSearch"
                            value={managerSearchTerm}
                            onChange={handleManagerSearch}
                            placeholder="Search by name or email..."
                            className="input-field"
                            disabled={selectedManager !== null}
                        />
                        {selectedManager && (
                            <button
                                type="button"
                                onClick={clearManagerSelection}
                                className="clear-selection-btn"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    
                    {isSearching && (
                        <div className="search-loading">Searching...</div>
                    )}
                    
                    {searchResults.length > 0 && !selectedManager && (
                        <div className="search-results">
                            {searchResults.map(manager => (
                                <div
                                    key={manager.manager_id}
                                    className="search-result-item"
                                    onClick={() => selectManager(manager)}
                                >
                                    <div className="manager-name">
                                        {manager.first_name} {manager.last_name}
                                    </div>
                                    <div className="manager-email">{manager.email}</div>
                                    {manager.phone && (
                                        <div className="manager-phone">0{manager.phone}</div>
                                    )}
                                    {manager.teams && (
                                        <div className="manager-teams">
                                            <strong>Teams:</strong> {manager.teams}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {selectedManager && (
                        <div className="selected-manager">
                            <div className="selected-manager-info">
                                <strong>Selected Manager:</strong> {selectedManager.first_name} {selectedManager.last_name}
                                <br />
                                <small>{selectedManager.email}</small>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {(formData.type !== 'friendly' && managerSearchMode === 'new') || formData.type === 'friendly' ? (
                <>
                    <div className="form-group">
                        <label htmlFor="firstName">First Name *</label>
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={adminData.firstName}
                            onChange={handleAdminChange}
                            required
                            placeholder="Enter first name"
                            className="input-field"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="lastName">Last Name *</label>
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={adminData.lastName}
                            onChange={handleAdminChange}
                            required
                            placeholder="Enter last name"
                            className="input-field"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={adminData.email}
                            onChange={handleAdminChange}
                            placeholder="Enter email (optional)"
                            className="input-field"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">Phone *</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={adminData.phone}
                            onChange={handleAdminChange}
                            required
                            placeholder="Enter phone number"
                            className="input-field"
                        />
                    </div>
                </>
            ) : null}

            {formData.type === 'squad' && (
                <div className="form-group checkbox-group">
                    <label>
                        <input
                            type="checkbox"
                            name="isPlayerManager"
                            checked={adminData.isPlayerManager}
                            onChange={handleAdminChange}
                        />
                        I am also a player (Player-Manager)
                    </label>
                </div>
            )}



            <div className="form-navigation">
                <button 
                    type="button" 
                    className="prev-stage-button"
                    onClick={handlePrevStage}
                >
                    ← Previous
                </button>
                <button 
                    type="submit" 
                    className="next-stage-button"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Registering...' : 'Complete Registration'}
                </button>
            </div>
        </form>
    );



    return (
        <div className="register-team-page">
            <div className="container">
                <Header />
                <div className="content-container">
                {renderStageIndicator()}
                
                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="success-message">
                        Team and admin registered successfully!
                    </div>
                )}

                {currentStage === 1 ? renderTeamForm() : renderAdminForm()}

                {showInviteModal && (
                    <div className="modal-overlay" onClick={() => {
                        setShowInviteModal(false);
                        navigate('/admin');
                    }}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <h2>Team Registration Complete</h2>
                            <p>The team has been registered successfully. Here's what you need to do next:</p>
                            <div className="invite-code">
                                <p><strong>Registration Code</strong>:&nbsp;<span>{inviteCode}</span></p>
                                <p className="instructions">Share this code with the team manager to complete their player profile setup.</p>
                                <button onClick={copyToClipboard}>Copy Registration Link</button>
                            </div>
                            
                            {/* Login Details for Player-Managers */}
                            {(adminLoginDetails || (formData.type === 'friendly' && adminData.phone)) && (
                                <div className="login-details">
                                    <h3>Login Details</h3>
                                    <p><strong>Phone Number</strong>:&nbsp;<span>{adminLoginDetails?.phone || adminData.phone}</span></p>
                                    <p><strong>Temporary Password</strong>:&nbsp;<span>{adminLoginDetails?.password || 'Contact support for password'}</span></p>
                                    <p className="instructions">Share these login details with the team organiser. They will be prompted to change their password on first login.</p>
                                    <button onClick={() => {
                                        const text = `Phone: ${adminLoginDetails?.phone || adminData.phone}\nPassword: ${adminLoginDetails?.password || 'Contact support for password'}`;
                                        navigator.clipboard.writeText(text);
                                        alert('Login details copied to clipboard!');
                                    }}>
                                        Copy Login Details
                                    </button>
                                </div>
                            )}
                            
                            {/* Welcome Page Links */}
                            {formData.type === 'academy' && teamId && (
                                <div className="academy-welcome-section">
                                    <h3>Academy Welcome Page</h3>
                                    <p>Share this link with your coaches to showcase FirstWhistle:</p>
                                    <div className="academy-link-box">
                                        <a 
                                            href={`${window.location.origin}/academy-welcome?team_id=${teamId}&team_name=${encodeURIComponent(formData.teamName)}`}
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="academy-welcome-link"
                                        >
                                            {window.location.origin}/academy-welcome?team_id={teamId}&team_name={encodeURIComponent(formData.teamName)}
                                        </a>
                                    </div>
                                    <p className="academy-note">This link showcases what FirstWhistle can do for your academy!</p>
                                </div>
                            )}
                            
                            {formData.type === 'friendly' && teamId && (
                                <div className="friendly-welcome-section">
                                    <h3>Friendly Team Welcome Page</h3>
                                    <p>Share this link with your team to showcase FirstWhistle:</p>
                                    <div className="friendly-link-box">
                                        <a 
                                            href={`${window.location.origin}/friendly-welcome?team_id=${teamId}&team_name=${encodeURIComponent(formData.teamName)}&organiser_name=${encodeURIComponent(adminData.firstName)}`}
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="friendly-welcome-link"
                                        >
                                            {window.location.origin}/friendly-welcome?team_id={teamId}&team_name={encodeURIComponent(formData.teamName)}&organiser_name={encodeURIComponent(adminData.firstName)}
                                        </a>
                                    </div>
                                    <p className="friendly-note">This link showcases what FirstWhistle can do for your friendly team!</p>
                                </div>
                            )}
                            
                            <div className="modal-actions">
                                <button onClick={() => {
                                    setShowInviteModal(false);
                                    navigate('/admin');
                                }}>
                                    Return to Admin Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showAdminModal && adminLoginDetails && (
                    <div className="modal-overlay" onClick={() => {
                        setShowAdminModal(false);
                        navigate('/admin');
                    }}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <h2>Team Registration Complete</h2>
                            {selectedManager ? (
                                <>
                                    <p>Team <strong>{formData.teamName}</strong> has been added and associated with coach <strong>{selectedManager.first_name} {selectedManager.last_name}</strong>.</p>
                                    <div className="invite-code">
                                        <p className="instructions">The existing coach account has been linked to the new team. No additional login details are required.</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p>The team manager has been registered successfully. Here are their login details:</p>
                                    <div className="invite-code">
                                        <p><strong>Phone Number</strong>:&nbsp;<span>{adminLoginDetails.phone}</span></p>
                                        <p><strong>Temporary Password</strong>:&nbsp;<span>{adminLoginDetails.password}</span></p>
                                        <p className="instructions">Share these details with the team manager. They will be prompted to change their password on first login.</p>
                                        <button onClick={() => {
                                            const text = `Phone: ${adminLoginDetails.phone}\nPassword: ${adminLoginDetails.password}`;
                                            navigator.clipboard.writeText(text);
                                            alert('Login details copied to clipboard!');
                                        }}>
                                            Copy Login Details
                                        </button>
                                    </div>
                                </>
                            )}
                            
                            {/* Academy Welcome Link for Academy teams */}
                            {formData.type === 'academy' && teamId && (
                                <div className="academy-welcome-section">
                                    <h3>Academy Welcome Page</h3>
                                    <p>Share this link with your coaches to showcase FirstWhistle:</p>
                                    <div className="academy-link-box">
                                        <a 
                                            href={`${window.location.origin}/academy-welcome?team_id=${teamId}&team_name=${encodeURIComponent(formData.teamName)}`}
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="academy-welcome-link"
                                        >
                                            {window.location.origin}/academy-welcome?team_id={teamId}&team_name={encodeURIComponent(formData.teamName)}
                                        </a>
                                    </div>
                                    <p className="academy-note">This link showcases what FirstWhistle can do for your academy!</p>
                                </div>
                            )}
                            
                            <div className="logo-upload-instructions">
                                <h3>Logo Upload Required</h3>
                                <p><strong>Team ID:</strong> {teamId}</p>
                                <p>Please upload the team logo to:</p>
                                <code>/api/images/teams/{teamId}/</code>
                                <p><strong>Required files:</strong></p>
                                <ul>
                                    <li><code>logo-dark.png</code> - Dark version of the logo</li>
                                    <li><code>logo-light.png</code> - Light version of the logo</li>
                                </ul>
                                <p className="logo-note"><strong>Note:</strong> Create the directory if it doesn't exist. Logo files should be PNG format and ideally 200x200 pixels or larger.</p>
                            </div>
                            
                            <div className="html-file-instructions">
                                <h3>Team-Specific Login Page</h3>
                                <p>To enable team-specific login pages, duplicate the HTML file:</p>
                                <code>cp public/77.html public/{teamId}.html</code>
                                <p><strong>Then update these lines in public/{teamId}.html:</strong></p>
                                <ul>
                                    <li><strong>Line 20:</strong> Update team name in title</li>
                                    <li><strong>Line 22:</strong> Update team name in meta description</li>
                                    <li><strong>Line 23:</strong> Update canonical URL</li>
                                    <li><strong>Line 33:</strong> Update localStorage team ID</li>
                                    <li><strong>Line 34:</strong> Update localStorage team name</li>
                                </ul>
                                <p className="html-note"><strong>Note:</strong> This enables iOS "Add to Home Screen" functionality with team branding. The app will display the team name when saved to home screen.</p>
                            </div>
                            <div className="modal-actions">
                                <button onClick={() => {
                                    setShowAdminModal(false);
                                    navigate('/admin');
                                }}>
                                    Return to Admin Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    </div>
    );
}

export default RegisterTeam; 